import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { useMemberAuth } from './contexts/MemberAuthContext';
import { useUserContext } from './contexts/UserContext';
import { useOnboarding } from './utils/useOnboarding';
import LoadingSpinner from './components/ui/LoadingSpinner';
import OnboardingError from './components/OnboardingError';

// Components
import Layout from './components/Layout';

// Pages
import HomePage from './pages/HomePage';
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import AuthCallbackPage from './pages/auth/AuthCallbackPage';
import OnboardingPage from './pages/auth/OnboardingPage';
import VerifyPage from './pages/auth/VerifyPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import AIPage from './pages/AIPage';
import ReachPage from './pages/ReachPage';
import DashboardPage from './pages/DashboardPage';
import BranchesPage from './pages/BranchesPage';
import MembersPage from './pages/MembersPage';
import PlansPage from './pages/PlansPage';
import PlansPreviewPage from './pages/PlansPreviewPage';
import PurchasesPage from './pages/PurchasesPage';
import ServicesPage from './pages/ServicesPage';
import ProductsPage from './pages/ProductsPage';
import SchedulePage from './pages/SchedulePage';
import ExpensesPage from './pages/ExpensesPage';
import GstReportPage from './pages/GstReportPage';
import TeamPage from './pages/TeamPage';
import AttendancePage from './pages/AttendancePage';
import NotificationsPage from './pages/NotificationsPage';
import WhatsAppNotificationsPage from './pages/WhatsAppNotificationsPage';
import CampaignsPage from './pages/CampaignsPage';
import MemberSelfServicePage from './pages/MemberSelfServicePage';
import MembershipManagementPage from './pages/MembershipManagementPage';
import GymSettingsPage from './pages/GymSettingsPage';
import UserProfilePage from './pages/UserProfilePage';
import SuperAdminLoginPage from './pages/saas/SuperAdminLoginPage';
import SuperAdminManagePage from './pages/saas/SuperAdminManagePage';
import GymDetailPage from './pages/saas/GymDetailPage';
import PricingShowcasePage from './pages/saas/PricingShowcasePage';
import ProtectedSuperAdminRoute from './components/ProtectedSuperAdminRoute';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import PayPage from './app/pay/PayPage';
import TermsAndConditions from './pages/TermsAndConditions';
import DataDeletionPage from './pages/DataDeletionPage';
import MemberProgressGymPage from './pages/MemberProgressGymPage';
import ReportsPage from './pages/ReportsPage';

import FeatureGuard from './components/FeatureGuard';
import RoleGuard from './components/RoleGuard';
import MemberProtectedRoute from './components/MemberProtectedRoute';
import MemberLayout from './app/member/components/MemberLayout';
import MemberLoginPage from './app/member/pages/MemberLoginPage';
import MemberHomePage from './app/member/pages/MemberHomePage';
import MemberCheckinsPage from './app/member/pages/MemberCheckinsPage';
import MemberCheckInPage from './app/member/pages/MemberCheckInPage';
import MemberProfilePage from './app/member/pages/MemberProfilePage';
import MemberDeleteAccountPage from './app/member/pages/MemberDeleteAccountPage';
import MemberBillingPage from './app/member/pages/MemberBillingPage';
import MemberInvoicePage from './app/member/pages/MemberInvoicePage';
import MemberBookPage from './app/member/pages/MemberBookPage';
import MemberCourtGridPage from './app/member/pages/MemberCourtGridPage';
import MemberBookingsPage from './app/member/pages/MemberBookingsPage';
import MemberProgressPage from './app/member/pages/MemberProgressPage';
import MemberDailyLogPage from './app/member/pages/MemberDailyLogPage';
import MemberMetricDetailPage from './app/member/pages/MemberMetricDetailPage';
import MemberPlansPage from './app/member/pages/MemberPlansPage';
import MemberPausesPage from './app/member/pages/MemberPausesPage';
import { MemberCoachesPage, MemberCoachPage } from './app/member/pages/MemberCoachesPage';
import MemberCalendarPage from './app/member/pages/MemberCalendarPage';
import MemberActivityPage from './app/member/pages/MemberActivityPage';
import MemberPlanTodayPage from './app/member/pages/plan/MemberPlanTodayPage';
import MemberPlanBuilderPage from './app/member/pages/plan/MemberPlanBuilderPage';
import MemberPlanRulesPage from './app/member/pages/plan/MemberPlanRulesPage';

function App() {
  const { user, loading: authLoading } = useAuth();
  const { member, loading: memberLoading } = useMemberAuth();
  // Use the isComplete helper for cleaner routing checks
  const { isComplete, loading: onboardingLoading, error: onboardingError, fetchStatus } = useOnboarding();
  const { userContext } = useUserContext();
  const location = useLocation();
  const isSaaS = location.pathname.startsWith('/saas');
  const isMemberRoute = location.pathname.startsWith('/app');

  // Extract gymId safely
  const gymId = userContext?.accessibleGyms?.[0];

  // Show loading state if necessary
  const isLoading = authLoading || (user && onboardingLoading && !isComplete && !isSaaS) || (isMemberRoute && memberLoading);

  // Helper component to prevent redirects while loading
  const SmartNavigate = ({ to }: { to: string }) => {
    if (isLoading) return null;
    return <Navigate to={to} replace />;
  };

  return (
    <>
      {isLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-50 bg-opacity-75">
          <div className="text-center">
            <LoadingSpinner size="lg" />
            <p className="mt-4 text-gray-600">{authLoading ? 'Authenticating user...' : 'Checking your setup...'}</p>
          </div>
        </div>
      )}
      <Routes>
        {/* Public Static Pages */}
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsAndConditions />} />
        <Route path="/data-deletion" element={<DataDeletionPage />} />
        {/* Payment links (WhatsApp) — public; the token is the only credential */}
        <Route path="/pay/:token" element={<PayPage />} />

        {/* Member App Routes */}
        <Route
          path="/app/login"
          element={
            user ? <SmartNavigate to="/dashboard" /> : member ? <SmartNavigate to="/app" /> : <MemberLoginPage />
          }
        />
        <Route
          path="/app"
          element={
            <MemberProtectedRoute>
              <MemberLayout />
            </MemberProtectedRoute>
          }
        >
          <Route index element={<MemberHomePage />} />
          <Route path="book" element={<MemberBookPage />} />
          <Route path="book/courts/:classId" element={<MemberCourtGridPage />} />
          <Route path="bookings" element={<MemberBookingsPage />} />
          <Route path="classes" element={<SmartNavigate to="/app/book" />} />
          <Route path="checkin" element={<MemberCheckInPage />} />
          <Route path="checkins" element={<MemberCheckinsPage />} />
          <Route path="billing" element={<MemberBillingPage />} />
          <Route path="billing/invoice/:purchaseId" element={<MemberInvoicePage />} />
          <Route path="profile" element={<MemberProfilePage />} />
          <Route path="progress" element={<MemberProgressPage />} />
          <Route path="progress/log" element={<MemberDailyLogPage />} />
          <Route path="progress/metric/:metricId" element={<MemberMetricDetailPage />} />
          <Route path="plans" element={<MemberPlansPage />} />
          <Route path="pauses" element={<MemberPausesPage />} />
          <Route path="coaches" element={<MemberCoachesPage />} />
          <Route path="coaches/:id" element={<MemberCoachPage />} />
          <Route path="calendar" element={<MemberCalendarPage />} />
          <Route path="activity" element={<MemberActivityPage />} />
          <Route path="session/:date" element={<MemberPlanTodayPage />} />
          <Route path="plan" element={<MemberPlanTodayPage />} />
          <Route path="plan/new" element={<MemberPlanBuilderPage />} />
          <Route path="plan/rules" element={<MemberPlanRulesPage />} />
        </Route>

        {/* Standalone member route — no MemberLayout chrome, own auth guard */}
        <Route
          path="/app/profile/delete"
          element={
            <MemberProtectedRoute>
              <MemberDeleteAccountPage />
            </MemberProtectedRoute>
          }
        />

        {/* Routing Decision Tree:
        1. If NOT authenticated: Show public routes (Login, Register, Home).
        2. If AUTHENTICATED: 
           a. If NOT onboarding complete: Redirect everything to /onboarding.
           b. If ONBOARDING COMPLETE: Show full app routes.
      */}

        {/* Super Admin Routes (separate from regular auth) */}
        <Route path="/saas/login" element={<SuperAdminLoginPage />} />
        <Route
          path="/saas/manage"
          element={
            <ProtectedSuperAdminRoute>
              <SuperAdminManagePage />
            </ProtectedSuperAdminRoute>
          }
        />
        <Route
          path="/saas/manage/gyms/:gymId"
          element={
            <ProtectedSuperAdminRoute>
              <GymDetailPage />
            </ProtectedSuperAdminRoute>
          }
        />
        <Route
          path="/saas/pricing"
          element={
            <ProtectedSuperAdminRoute>
              <PricingShowcasePage />
            </ProtectedSuperAdminRoute>
          }
        />

        {/* Public routes (User is NOT authenticated) */}
        {!user ? (
          <>
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={member ? <SmartNavigate to="/app" /> : <LoginPage />} />
            <Route path="/register" element={member ? <SmartNavigate to="/app" /> : <RegisterPage />} />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />
            <Route path="/auth/verify" element={<VerifyPage />} />
            <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
            {/* Catch all unauthenticated routes */}
            <Route path="*" element={<SmartNavigate to={member ? '/app' : '/'} />} />
          </>
        ) : (
          /* Authenticated routes (User is logged in) */
          <>
            {/* Onboarding status check failed - show error with retry */}
            {onboardingError && !isSaaS ? (
              <>
                <Route
                  path="*"
                  element={
                    <OnboardingError
                      error={onboardingError}
                      onRetry={() => fetchStatus()}
                      retrying={onboardingLoading}
                    />
                  }
                />
              </>
            ) : !isComplete && !isSaaS ? (
              /* Onboarding required (User is authenticated but setup is NOT complete) 
                SKIP this check for SaaS routes - Super Admins don't do onboarding
              */
              <>
                <Route path="/onboarding" element={<OnboardingPage />} />
                <Route path="/auth/callback" element={<AuthCallbackPage />} />
                {/* Catch all routes and redirect to onboarding */}
                <Route path="*" element={<SmartNavigate to="/onboarding" />} />
              </>
            ) : (
              /* Onboarding complete (User is authenticated and setup IS complete) */
              <>
                {/* Allow authenticated users to access reset password page (e.g. from recovery email) */}
                <Route path="/auth/reset-password" element={<ResetPasswordPage />} />

                {/* Redirect any attempts to access public or onboarding routes to the dashboard */}
                <Route path="/" element={<SmartNavigate to="/dashboard" />} />
                <Route path="/login" element={<SmartNavigate to="/dashboard" />} />
                <Route path="/register" element={<SmartNavigate to="/dashboard" />} />
                <Route path="/onboarding" element={<SmartNavigate to="/dashboard" />} />
                <Route path="/auth/callback" element={<AuthCallbackPage />} /> {/* Allow callback for post-login flows */}

                <Route
                  path="/ai"
                  element={
                    <Layout pageTitle="AI Insights" hideTopBar={true}>
                      <FeatureGuard featureKey="dashboard" featureName="AI Insights">
                        <AIPage />
                      </FeatureGuard>
                    </Layout>
                  }
                />
                <Route
                  path="/reach"
                  element={
                    <Layout pageTitle="Reach AI" hideTopBar={true}>
                      <FeatureGuard featureKey="dashboard" featureName="Reach AI">
                        <ReachPage />
                      </FeatureGuard>
                    </Layout>
                  }
                />
                <Route
                  path="/expenses"
                  element={
                    <Layout pageTitle="Expenses" hideTopBar={true}>
                      <FeatureGuard featureKey="expense_tracking" featureName="Expense Tracking">
                        <ExpensesPage />
                      </FeatureGuard>
                    </Layout>
                  }
                />
                <Route
                  path="/gst-report"
                  element={
                    <Layout pageTitle="GST Report" hideTopBar={true}>
                      <FeatureGuard featureKey="gst_reports" featureName="GST Reports">
                        <GstReportPage />
                      </FeatureGuard>
                    </Layout>
                  }
                />
                <Route
                  path="/dashboard"
                  element={
                    <Layout pageTitle="Dashboard" hideTopBar={true}>
                      <FeatureGuard featureKey="dashboard" featureName="Dashboard">
                        <DashboardPage />
                      </FeatureGuard>
                    </Layout>
                  }
                />
                <Route
                  path="/branches"
                  element={
                    <Layout>
                      <FeatureGuard featureKey="branches" featureName="Branches">
                        <BranchesPage />
                      </FeatureGuard>
                    </Layout>
                  }
                />
                <Route
                  path="/members/:memberId/progress"
                  element={
                    <Layout pageTitle="Member Progress">
                      <MemberProgressGymPage />
                    </Layout>
                  }
                />
                <Route
                  path="/members"
                  element={
                    <Layout pageTitle="Members" pageSubtitle="Manage your gym members and their memberships">
                      <FeatureGuard featureKey="members" featureName="Members">
                        <MembersPage />
                      </FeatureGuard>
                    </Layout>
                  }
                />
                <Route
                  path="/reports"
                  element={
                    <Layout pageTitle="Reports" pageSubtitle="Expiry, inactivity, revenue and member exports">
                      <FeatureGuard featureKey="reports" featureName="Reports">
                        <ReportsPage />
                      </FeatureGuard>
                    </Layout>
                  }
                />
                <Route
                  path="/plans"
                  element={
                    <Layout>
                      <FeatureGuard featureKey="plans" featureName="Plans">
                        <PlansPage />
                      </FeatureGuard>
                    </Layout>
                  }
                />
                <Route
                  path="/plans/preview"
                  element={
                    <Layout pageTitle="Plan preview">
                      <PlansPreviewPage />
                    </Layout>
                  }
                />
                <Route
                  path="/purchases"
                  element={
                    <Layout>
                      <FeatureGuard featureKey="purchases" featureName="Purchases">
                        <PurchasesPage />
                      </FeatureGuard>
                    </Layout>
                  }
                />
                <Route
                  path="/services"
                  element={
                    <Layout pageTitle="Services" pageSubtitle="Manage your service catalog">
                      <FeatureGuard featureKey="purchases" featureName="Services">
                        <ServicesPage />
                      </FeatureGuard>
                    </Layout>
                  }
                />
                <Route
                  path="/products"
                  element={
                    <Layout pageTitle="Products" pageSubtitle="Manage your product catalog">
                      <FeatureGuard featureKey="purchases" featureName="Products">
                        <ProductsPage />
                      </FeatureGuard>
                    </Layout>
                  }
                />
                <Route
                  path="/classes"
                  element={<Navigate to="/schedule" replace />}
                />
                <Route
                  path="/schedule"
                  element={
                    <Layout pageTitle="Bookings" pageSubtitle="Classes, spa, personal training, consults & courts">
                      <FeatureGuard featureKey="class_scheduling" featureName="Bookings">
                        <SchedulePage />
                      </FeatureGuard>
                    </Layout>
                  }
                />
                <Route
                  path="/team"
                  element={
                    <Layout pageTitle="Team Management" pageSubtitle="Manage your gym team members">
                      <FeatureGuard featureKey="team" featureName="Team">
                        <TeamPage />
                      </FeatureGuard>
                    </Layout>
                  }
                />
                <Route
                  path="/hrms"
                  element={
                    <Layout pageTitle="HRMS" pageSubtitle="Attendance, leaves, and payslips">
                      <FeatureGuard featureKey="hrms" featureName="HRMS">
                        <TeamPage mode="hrms" />
                      </FeatureGuard>
                    </Layout>
                  }
                />
                <Route
                  path="/attendance"
                  element={
                    <Layout pageTitle="Attendance" pageSubtitle="Scan QR codes to record member attendance">
                      <FeatureGuard featureKey="attendance" featureName="Attendance">
                        <AttendancePage />
                      </FeatureGuard>
                    </Layout>
                  }
                />
                <Route
                  path="/notifications"
                  element={
                    <Layout>
                      <FeatureGuard featureKey="notifications" featureName="Notifications">
                        <NotificationsPage />
                      </FeatureGuard>
                    </Layout>
                  }
                />
                <Route
                  path="/notifications/whatsapp"
                  element={
                    <Layout pageTitle="WhatsApp Notifications" pageSubtitle="Create and send WhatsApp campaigns">
                      <FeatureGuard featureKey="notifications" featureName="WhatsApp Notifications">
                        <WhatsAppNotificationsPage gymId={gymId || ''} />
                      </FeatureGuard>
                    </Layout>
                  }
                />
                <Route
                  path="/notifications/campaigns"
                  element={
                    <Layout pageTitle="Campaigns" pageSubtitle="View and manage your WhatsApp campaigns">
                      <FeatureGuard featureKey="notifications" featureName="Campaigns">
                        <CampaignsPage gymId={gymId || ''} />
                      </FeatureGuard>
                    </Layout>
                  }
                />
                <Route
                  path="/my-membership"
                  element={
                    <Layout pageTitle="My Membership" pageSubtitle="Manage your membership and features">
                      <MemberSelfServicePage />
                    </Layout>
                  }
                />
                <Route
                  path="/membership-management"
                  element={
                    <Layout pageTitle="Membership Management" pageSubtitle="Manage pauses, guest passes, and transfers">
                      <MembershipManagementPage />
                    </Layout>
                  }
                />
                <Route
                  path="/settings/gym"
                  element={
                    <Layout pageTitle="Gym Settings" pageSubtitle="Manage your gym's official details and profiles">
                      <FeatureGuard featureKey="settings" featureName="Gym Settings">
                        <RoleGuard allowedRoles="owner">
                          <GymSettingsPage />
                        </RoleGuard>
                      </FeatureGuard>
                    </Layout>
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <Layout pageTitle="My Profile" pageSubtitle="Manage your profile and account settings">
                      <UserProfilePage />
                    </Layout>
                  }
                />
                {/* Catch all authenticated routes and redirect to dashboard */}
                <Route path="*" element={<SmartNavigate to="/dashboard" />} />
              </>
            )}
          </>
        )}
      </Routes>
    </>
  );
}

export default App;
